import { useMemo, useState } from 'react'
import {
  Calculator,
  Delete,
  Eraser,
  ChevronDown,
  GitBranch,
  Hash,
  Quote,
  Plus,
  Sigma,
  SquareRadical,
  CalendarClock,
  CalendarDays,
  Undo2,
  Redo2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  OPERATORS,
  COMPARISON_OPERATORS,
  evaluateFormula,
  formatCalculated,
  formulaToHuman,
  splitFormulaTokens,
  toStringLiteral,
  type DisplayFormat,
} from '@/lib/formula'
import { ConditionBuilder } from './ConditionBuilder'
import { AggregateBuilder } from './AggregateBuilder'
import { DateFunctionBuilder } from './DateFunctionBuilder'
import { MathFunctionBuilder } from './MathFunctionBuilder'
import { FormulaTokenStrip } from './FormulaTokenStrip'
import { FormulaTokenChip } from './FormulaTokenChip'
import { useFormulaChipEditor } from './useFormulaChipEditor'

interface Props {
  formula: string
  displayFormat: DisplayFormat
  onChange: (changes: { formula?: string; displayFormat?: DisplayFormat }) => void
}

const OP_SYMBOL: Record<string, string> = {
  '+': '+', '-': '−', '*': '×', '/': '÷', '(': '(', ')': ')',
  '==': '==', '!=': '≠', '<': '<', '<=': '≤', '>': '>', '>=': '≥',
  ',': ',', 'IF': 'IF', 'AND': 'AND', 'OR': 'OR', 'ANY': 'ANY', 'ALL': 'ALL',
  'SUM': 'SUM', 'MAX': 'MAX',
  'LOG10': 'LOG10', 'ABS': 'ABS', 'CEILING': 'CEILING', 'FLOOR': 'FLOOR', 'ROUND': 'ROUND',
}

const DISPLAY_FORMAT_LABELS: Record<DisplayFormat, string> = {
  decimal3: 'Decimal (0.000)',
  decimal2: 'Decimal (0.00)',
  number: 'Whole number',
  date: 'Date (Aug 15, 2026)',
  datetime: 'Date & time (Aug 15, 2026 2:30 PM)',
}

/**
 * Formula builder for Field Library calculated fields.
 *
 * Library fields don't live on a specific form, so instead of referencing
 * form field UUIDs (like FormulaBuilder does), this builder uses named
 * placeholders — e.g. {weight}, {dose}. When the field is inserted onto a
 * form the re-map step translates each placeholder to an actual form field ID.
 */
export function LibraryFormulaBuilder({ formula, displayFormat, onChange }: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [literal, setLiteral] = useState('')
  const [placeholderName, setPlaceholderName] = useState('')
  const [sampleValues, setSampleValues] = useState<Record<string, string>>({})

  const editor = useFormulaChipEditor(formula, f => onChange({ formula: f }))
  const tokens = editor.tokens

  // Unique placeholder names currently referenced in the formula.
  const placeholders = useMemo(() => {
    const seen: string[] = []
    for (const m of formula.matchAll(/\{([^}]+)\}/g)) {
      if (!seen.includes(m[1])) seen.push(m[1])
    }
    return seen
  }, [formula])

  // In the library context, the placeholder name IS the label.
  const labelById = useMemo(() => {
    const m: Record<string, string> = {}
    for (const p of placeholders) m[p] = p
    return m
  }, [placeholders])

  const addPlaceholder = (name: string) => {
    const clean = name.trim().replace(/\s+/g, '_')
    if (!clean) return
    editor.insert([`{${clean}}`])
    setPlaceholderName('')
  }

  const addOperator = (op: string) => editor.insert([op])

  // Structured IF editor — emits a complete IF(...) expression in one piece.
  const [ifOpen, setIfOpen] = useState(false)
  // Structured SUM/MAX editor — same one-piece-insert approach as IF.
  const [aggOpen, setAggOpen] = useState<'SUM' | 'MAX' | null>(null)
  // Structured DATEADD/DATEDIFF editor — same one-piece-insert approach.
  const [dateFnOpen, setDateFnOpen] = useState<'DATEADD' | 'DATEDIFF' | null>(null)
  // Structured LOG10/ABS/CEILING/FLOOR/ROUND editor — one panel for all five,
  // so the operator row keeps a single button instead of gaining five.
  const [mathFnOpen, setMathFnOpen] = useState(false)

  const addToday = () => editor.insert(['TODAY', '(', ')'])
  const addNow = () => editor.insert(['NOW', '(', ')'])

  const addNumber = () => {
    const t = literal.trim()
    if (t === '' || !Number.isFinite(Number(t))) return
    editor.insert([t])
    setLiteral('')
  }

  const addText = () => {
    if (literal === '') return
    editor.insert([toStringLiteral(literal)])
    setLiteral('')
  }

  const literalIsNumber = literal.trim() !== '' && Number.isFinite(Number(literal.trim()))

  const preview = useMemo(
    () => formatCalculated(evaluateFormula(formula, sampleValues), displayFormat),
    [formula, sampleValues, displayFormat],
  )

  const human = formula ? formulaToHuman(formula, labelById) : ''

  return (
    <div className='space-y-3'>
      <Label className='flex items-center gap-1.5'>
        <Calculator className='h-3.5 w-3.5' />
        Formula
      </Label>
      <p className='text-[11px] text-muted-foreground'>
        Use named placeholders like <span className='font-mono'>{'{weight}'}</span>,{' '}
        <span className='font-mono'>{'{dose}'}</span>. Each placeholder is mapped to a real form
        field when this library field is inserted.
      </p>

      {/* Visual token display — click a gap to place the cursor, click a chip
          to replace or remove it in place. */}
      <FormulaTokenStrip
        tokens={tokens}
        caret={editor.caret}
        onCaretClick={editor.setCaret}
        emptyHint='Add a placeholder or operator below…'
        renderToken={(tok, i) => {
          const refMatch = tok.match(/^\{(.+)\}$/)
          const chipProps = {
            token: tok,
            isSelected: editor.selectedIndex === i,
            onOpenChange: (open: boolean) => editor.setSelectedIndex(open ? i : null),
            onReplace: (newToken: string) => editor.replace(i, newToken),
            onRemove: () => editor.removeAt(i),
          }
          if (refMatch) {
            return (
              <FormulaTokenChip
                {...chipProps}
                className='bg-primary/10 text-primary'
                refEditor={replace => (
                  <PlaceholderRefEditor initialName={refMatch[1]} onPick={name => replace(`{${name}}`)} />
                )}
              >
                {refMatch[1]}
              </FormulaTokenChip>
            )
          }
          if (tok.startsWith('"')) {
            return (
              <FormulaTokenChip {...chipProps} className='bg-emerald-100 text-emerald-700'>
                {tok}
              </FormulaTokenChip>
            )
          }
          if (/^\d/.test(tok)) {
            return (
              <FormulaTokenChip {...chipProps} className='bg-amber-100 text-amber-700'>
                {tok}
              </FormulaTokenChip>
            )
          }
          return (
            <FormulaTokenChip {...chipProps} className='font-semibold text-muted-foreground'>
              {OP_SYMBOL[tok] ?? tok}
            </FormulaTokenChip>
          )
        }}
      />

      {/* Arithmetic operators + IF + backspace/clear */}
      {/* Wraps rather than overflowing: this row carries 6 operators + IF/SUM/MAX
          + the edit actions, which no longer fit one line in the narrow field
          dialog when zoomed or on mobile. */}
      <div className='flex flex-wrap items-center gap-1'>
        {OPERATORS.map(op => (
          <Button
            key={op.token}
            type='button'
            size='icon'
            variant='outline'
            className='h-7 w-7 shrink-0 text-sm'
            title={op.label}
            onClick={() => addOperator(op.token)}
          >
            {op.symbol}
          </Button>
        ))}
        <Button
          type='button'
          size='sm'
          variant={ifOpen ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build an IF(condition, then, else) expression'
          onClick={() => setIfOpen(o => !o)}
        >
          <GitBranch className='h-3 w-3' />
          IF
        </Button>
        <Button
          type='button'
          size='sm'
          variant={aggOpen === 'SUM' ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a SUM(e1, e2, …) expression'
          onClick={() => setAggOpen(o => (o === 'SUM' ? null : 'SUM'))}
        >
          <Sigma className='h-3 w-3' />
          SUM
        </Button>
        <Button
          type='button'
          size='sm'
          variant={aggOpen === 'MAX' ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a MAX(e1, e2, …) expression'
          onClick={() => setAggOpen(o => (o === 'MAX' ? null : 'MAX'))}
        >
          <Sigma className='h-3 w-3' />
          MAX
        </Button>
        <Button
          type='button'
          size='sm'
          variant={mathFnOpen ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a LOG10 / ABS / CEILING / FLOOR / ROUND expression'
          onClick={() => setMathFnOpen(o => !o)}
        >
          <SquareRadical className='h-3 w-3' />
          MATH
        </Button>
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title="Insert today's date"
          onClick={addToday}
        >
          <CalendarDays className='h-3 w-3' />
          TODAY
        </Button>
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Insert the current date and time'
          onClick={addNow}
        >
          <CalendarDays className='h-3 w-3' />
          NOW
        </Button>
        <Button
          type='button'
          size='sm'
          variant={dateFnOpen === 'DATEADD' ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a DATEADD(date, amount, unit) expression'
          onClick={() => setDateFnOpen(o => (o === 'DATEADD' ? null : 'DATEADD'))}
        >
          <CalendarClock className='h-3 w-3' />
          DATEADD
        </Button>
        <Button
          type='button'
          size='sm'
          variant={dateFnOpen === 'DATEDIFF' ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a DATEDIFF(start, end, unit) expression'
          onClick={() => setDateFnOpen(o => (o === 'DATEDIFF' ? null : 'DATEDIFF'))}
        >
          <CalendarClock className='h-3 w-3' />
          DATEDIFF
        </Button>
        {/* ml-auto keeps these right-aligned on one line, and keeps them
            together as a unit once the row wraps. */}
        <div className='ml-auto flex shrink-0 items-center gap-1'>
          <Button
            type='button'
            size='icon'
            variant='ghost'
            className='h-7 w-7 text-muted-foreground'
            title='Undo'
            onClick={editor.undo}
            disabled={!editor.canUndo}
          >
            <Undo2 className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            size='icon'
            variant='ghost'
            className='h-7 w-7 text-muted-foreground'
            title='Redo'
            onClick={editor.redo}
            disabled={!editor.canRedo}
          >
            <Redo2 className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            size='icon'
            variant='ghost'
            className='h-7 w-7 text-muted-foreground'
            title='Remove last token'
            onClick={editor.backspace}
            disabled={tokens.length === 0}
          >
            <Delete className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            size='icon'
            variant='ghost'
            className='h-7 w-7 text-muted-foreground'
            title='Clear formula'
            onClick={editor.clear}
            disabled={tokens.length === 0}
          >
            <Eraser className='h-3.5 w-3.5' />
          </Button>
        </div>
      </div>

      {/* Comparison + logical operators (for use inside IF conditions) */}
      <div className='flex flex-wrap items-center gap-1'>
        <span className='mr-1 shrink-0 text-[11px] text-muted-foreground'>Compare:</span>
        {COMPARISON_OPERATORS.map(op => (
          <Button
            key={op.token}
            type='button'
            size='icon'
            variant='outline'
            className='h-6 w-7 shrink-0 text-xs'
            title={op.label}
            onClick={() => addOperator(op.token)}
          >
            {op.symbol}
          </Button>
        ))}
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='h-6 shrink-0 px-2 text-xs'
          title='Logical AND — both conditions must be true'
          onClick={() => addOperator('AND')}
        >
          AND
        </Button>
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='h-6 shrink-0 px-2 text-xs'
          title='Logical OR — either condition must be true'
          onClick={() => addOperator('OR')}
        >
          OR
        </Button>
      </div>

      {/* Structured IF editor — placeholders as operands, new ones typable inline */}
      {ifOpen && (
        <ConditionBuilder
          operands={placeholders.map(p => ({ id: p, label: p }))}
          allowCustomRefs
          onInsert={f => {
            editor.insert(splitFormulaTokens(f))
            setIfOpen(false)
          }}
          onCancel={() => setIfOpen(false)}
        />
      )}

      {/* Structured SUM/MAX editor — placeholders as operands, new ones typable inline */}
      {aggOpen && (
        <AggregateBuilder
          fn={aggOpen}
          operands={placeholders.map(p => ({ id: p, label: p }))}
          allowCustomRefs
          onInsert={f => {
            editor.insert(splitFormulaTokens(f))
            setAggOpen(null)
          }}
          onCancel={() => setAggOpen(null)}
        />
      )}

      {/* Structured DATEADD/DATEDIFF editor — placeholders as operands, new ones typable inline */}
      {dateFnOpen && (
        <DateFunctionBuilder
          fn={dateFnOpen}
          operands={placeholders.map(p => ({ id: p, label: p }))}
          allowCustomRefs
          onInsert={f => {
            editor.insert(splitFormulaTokens(f))
            setDateFnOpen(null)
          }}
          onCancel={() => setDateFnOpen(null)}
        />
      )}

      {/* Structured math editor — placeholders as operands, new ones typable inline */}
      {mathFnOpen && (
        <MathFunctionBuilder
          operands={placeholders.map(p => ({ id: p, label: p }))}
          allowCustomRefs
          onInsert={f => {
            editor.insert(splitFormulaTokens(f))
            setMathFnOpen(false)
          }}
          onCancel={() => setMathFnOpen(false)}
        />
      )}

      {/* Named placeholder section */}
      <div className='space-y-1.5'>
        <p className='text-xs text-muted-foreground'>Add a field placeholder:</p>
        <div className='flex items-center gap-1.5'>
          <Input
            value={placeholderName}
            onChange={e => setPlaceholderName(e.target.value)}
            placeholder='e.g. weight, dose, age'
            className='h-8 text-sm'
            onKeyDown={e => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              addPlaceholder(placeholderName)
            }}
          />
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-8 shrink-0 px-2'
            disabled={!placeholderName.trim()}
            onClick={() => addPlaceholder(placeholderName)}
          >
            <Plus className='mr-1 h-3.5 w-3.5' />
            Add
          </Button>
        </div>
        {/* Re-clickable chips for already-defined placeholders */}
        {placeholders.length > 0 && (
          <div className='flex flex-wrap gap-1.5 pt-0.5'>
            {placeholders.map(p => (
              <button
                key={p}
                type='button'
                onClick={() => editor.insert([`{${p}}`])}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs transition-colors',
                  'hover:border-primary hover:bg-primary/5',
                )}
                title={`Insert {${p}}`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
        <p className='text-[11px] text-muted-foreground'>
          Each placeholder is mapped to a form field when the library field is inserted. Use
          descriptive names — they appear in the mapping dialog.
        </p>
      </div>

      {/* Fixed literal values */}
      <div className='space-y-1.5'>
        <p className='text-xs text-muted-foreground'>Add a fixed value:</p>
        <div className='flex items-center gap-1.5'>
          <Input
            value={literal}
            onChange={e => setLiteral(e.target.value)}
            placeholder='e.g. 100 or " mL"'
            className='h-8 text-sm'
            onKeyDown={e => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              if (literalIsNumber) addNumber()
              else addText()
            }}
          />
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-8 shrink-0 px-2'
            title='Insert as a number'
            disabled={!literalIsNumber}
            onClick={addNumber}
          >
            <Hash className='mr-1 h-3.5 w-3.5' />
            Number
          </Button>
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-8 shrink-0 px-2'
            title='Insert as text (for joining with +)'
            disabled={literal === ''}
            onClick={addText}
          >
            <Quote className='mr-1 h-3.5 w-3.5' />
            Text
          </Button>
        </div>
        <p className='text-[11px] text-muted-foreground'>
          Use <span className='font-semibold'>+</span> with text to join values, e.g.{' '}
          <span className='font-mono'>"12.5 mL"</span>.
        </p>
      </div>

      {/* Human-readable summary */}
      {human && (
        <div className='rounded-md bg-muted/40 px-2.5 py-2'>
          <p className='text-[11px] uppercase tracking-wide text-muted-foreground'>Summary</p>
          <p className='text-sm font-medium break-words'>{human}</p>
        </div>
      )}

      {/* Display format */}
      <div className='space-y-1.5'>
        <Label>Result format</Label>
        <Select
          value={displayFormat}
          onValueChange={v => onChange({ displayFormat: v as DisplayFormat })}
        >
          <SelectTrigger className='h-8 text-sm'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(DISPLAY_FORMAT_LABELS) as DisplayFormat[]).map(fmt => (
              <SelectItem key={fmt} value={fmt}>
                {DISPLAY_FORMAT_LABELS[fmt]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className='text-[11px] text-muted-foreground'>
          Numeric formats apply to number results; Date / Date &amp; time format a
          DATEADD()/date-ref result. A non-matching result is shown as-is.
        </p>
      </div>

      {/* Live preview with sample values */}
      {placeholders.length > 0 && (
        <div className='space-y-2 rounded-md border bg-card px-2.5 py-2.5'>
          <p className='text-[11px] uppercase tracking-wide text-muted-foreground'>Live preview</p>
          {placeholders.map(id => (
            <div key={id} className='flex items-center gap-2'>
              <Label className='flex-1 truncate text-xs font-normal text-muted-foreground'>
                {id}
              </Label>
              <Input
                type='text'
                value={sampleValues[id] ?? ''}
                placeholder='value'
                className='h-7 w-24 text-sm'
                onChange={e => setSampleValues(prev => ({ ...prev, [id]: e.target.value }))}
              />
            </div>
          ))}
          <div className='flex items-center justify-between border-t pt-2'>
            <span className='text-xs text-muted-foreground'>Result</span>
            <span className='font-mono text-sm font-semibold'>= {preview}</span>
          </div>
        </div>
      )}

      {/* Advanced: raw formula */}
      <div>
        <button
          type='button'
          onClick={() => setShowAdvanced(s => !s)}
          className='flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
        >
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform', showAdvanced && 'rotate-180')}
          />
          Formula (advanced)
        </button>
        {showAdvanced && (
          <div className='mt-1.5 space-y-1'>
            <pre className='overflow-x-auto rounded bg-muted px-2.5 py-2 text-[11px] font-mono text-muted-foreground'>
              {formula || '(empty)'}
            </pre>
            <p className='text-[11px] text-muted-foreground'>
              Raw formula stored in the library. Placeholders use stable names; the server
              re-maps them to form field IDs at insertion time.
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Conditional syntax:{' '}
              <span className='font-mono'>IF(condition, then_value, else_value)</span> — e.g.{' '}
              <span className='font-mono'>
                IF(&#123;weight&#125; &gt; 0, &#123;dose&#125; / &#123;weight&#125;, 0)
              </span>
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Logical functions: <span className='font-mono'>ALL(c1, c2, …)</span> (every
              condition true) and <span className='font-mono'>ANY(c1, c2, …)</span> (at least
              one true) — usable anywhere a condition goes.
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Aggregate functions: <span className='font-mono'>SUM(e1, e2, …)</span> (sum of
              the values, blanks skipped) and <span className='font-mono'>MAX(e1, e2, …)</span>{' '}
              (the largest value) — insert with the SUM / MAX buttons above.
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Math functions: <span className='font-mono'>LOG10(x)</span> (base-10 logarithm —
              blank when x is zero or negative), <span className='font-mono'>ABS(x)</span>,{' '}
              <span className='font-mono'>CEILING(x)</span> /{' '}
              <span className='font-mono'>FLOOR(x)</span> (round to a whole number, up / down),
              and <span className='font-mono'>ROUND(x, decimals)</span> (round mid-formula,
              halves away from zero) — insert with the MATH button above.
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Date functions: <span className='font-mono'>TODAY()</span> /{' '}
              <span className='font-mono'>NOW()</span>,{' '}
              <span className='font-mono'>DATEADD(date, amount, unit)</span>, and{' '}
              <span className='font-mono'>DATEDIFF(start, end, unit)</span> — insert with the
              buttons above. Date and Date &amp; Time fields also support{' '}
              <span className='font-mono'>&lt; &lt;= &gt; &gt;=</span> via the Compare row.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

/** Replace-in-place editor for a placeholder ref chip — renames the
 *  placeholder (same cleanup as `addPlaceholder`: trimmed, spaces → `_`). */
function PlaceholderRefEditor({
  initialName,
  onPick,
}: {
  initialName: string
  onPick: (name: string) => void
}) {
  const [name, setName] = useState(initialName)
  const clean = name.trim().replace(/\s+/g, '_')

  return (
    <div className='flex items-center gap-1.5'>
      <Input
        autoFocus
        value={name}
        onChange={e => setName(e.target.value)}
        className='h-8 text-sm'
        onKeyDown={e => {
          if (e.key !== 'Enter' || !clean) return
          e.preventDefault()
          onPick(clean)
        }}
      />
      <Button type='button' size='sm' disabled={!clean} onClick={() => onPick(clean)}>
        Apply
      </Button>
    </div>
  )
}
